import { useState, useEffect } from "react";
import { X, Loader2, ExternalLink, DollarSign, ArrowDownCircle, ArrowUpCircle, RotateCcw } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

/**
 * SupplierReconciliationDrawer — opens on demand from AdminSupplierApprovals.
 * Fetches GET /api/admin/suppliers/{user_id}/reconciliation and renders:
 *  - Ledger totals (gross, platform fees, supplier earnings, refunds, net owed)
 *  - Payout events (from payouts collection)
 *  - Transfer events (from transfers collection)
 *  - Refund history (from refunds)
 */
export default function SupplierReconciliationDrawer({ supplier, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/admin/suppliers/${supplier.user_id}/reconciliation`);
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setErr(formatApiError(e));
        toast.error(formatApiError(e));
      }
    })();
    return () => { cancelled = true; };
  }, [supplier.user_id]);

  const currency = (n) => `$${Number(n || 0).toFixed(2)}`;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
      data-testid="reconciliation-drawer"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-[720px] bg-ink-surface border border-ink-border rounded-t-2xl sm:rounded-2xl overflow-hidden flex flex-col max-h-[92vh]"
      >
        <div className="px-4 py-3 border-b border-ink-border flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-brand-accent/15 text-brand-accent flex items-center justify-center border border-brand-accent/30">
            <DollarSign size={16} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase font-black text-ink-textDim tracking-widest">// RECONCILIATION</div>
            <div className="font-black text-sm truncate">{supplier.name}</div>
            <div className="text-[10px] text-ink-textDim truncate">{supplier.email}</div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-ink-surface2 flex items-center justify-center" data-testid="reconciliation-close">
            <X size={14} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-ink-bg" data-testid="reconciliation-body">
          {err && <div className="text-red-400 text-sm">{err}</div>}
          {!data && !err && (
            <div className="text-center py-10"><Loader2 size={16} className="inline animate-spin text-ink-textDim" /></div>
          )}
          {data && (
            <>
              {/* Ledger */}
              <div className="bg-ink-surface border border-ink-border rounded-2xl p-4">
                <div className="text-[10px] uppercase font-black text-ink-textDim mb-2">Ledger</div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <Metric label="Gross paid" value={currency(data.ledger.gross_paid)} testid="ledger-gross" />
                  <Metric label="Platform fees" value={currency(data.ledger.platform_fees)} tone="text-emerald-300" testid="ledger-platform-fees" />
                  <Metric label="Supplier earnings" value={currency(data.ledger.supplier_earnings)} tone="text-brand-accent" testid="ledger-supplier-earnings" />
                  <Metric label="Refunded" value={currency(data.ledger.refunded_amount)} tone="text-red-300" testid="ledger-refunded" />
                  <Metric label="Net owed" value={currency(data.ledger.net_supplier_owed)} tone="text-white font-black" testid="ledger-net" />
                  <Metric label="Orders paid" value={`${data.ledger.paid_orders}/${data.ledger.total_orders}`} testid="ledger-orders" />
                </div>
              </div>

              <EventList
                title="Payouts"
                icon={<ArrowDownCircle size={12} />}
                items={data.payouts}
                testidPrefix="payout"
                renderRow={(p) => (
                  <>
                    <div className="font-black text-white">{currency((p.amount || 0) / 100)}</div>
                    <div className="text-[10px] uppercase text-ink-textDim">{p.status}</div>
                  </>
                )}
                emptyMsg="No payouts recorded yet."
              />

              <EventList
                title="Transfers"
                icon={<ArrowUpCircle size={12} />}
                items={data.transfers}
                testidPrefix="transfer"
                renderRow={(t) => (
                  <>
                    <div className="font-black text-white">{currency((t.amount || 0) / 100)}</div>
                    <div className="text-[10px] uppercase text-ink-textDim">{t.type.replace("transfer.", "")}{t.reversed ? " · reversed" : ""}</div>
                  </>
                )}
                emptyMsg="No transfers recorded yet."
              />

              <EventList
                title="Refunds"
                icon={<RotateCcw size={12} />}
                items={data.refunds}
                testidPrefix="refund"
                renderRow={(r) => (
                  <>
                    <div className="font-black text-white">{currency(r.amount)}</div>
                    <div className="text-[10px] uppercase text-ink-textDim">{r.flow} · {r.status}</div>
                    {r.reason && <div className="text-[10px] text-ink-textDim italic mt-0.5">"{r.reason}"</div>}
                  </>
                )}
                emptyMsg="No refunds on this supplier's orders."
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value, tone = "text-white", testid }) {
  return (
    <div className="bg-ink-bg border border-ink-border rounded-xl p-2" data-testid={testid}>
      <div className="text-[10px] uppercase font-black text-ink-textDim">{label}</div>
      <div className={`text-lg font-black ${tone}`}>{value}</div>
    </div>
  );
}

function EventList({ title, icon, items, testidPrefix, renderRow, emptyMsg }) {
  return (
    <div className="bg-ink-surface border border-ink-border rounded-2xl overflow-hidden" data-testid={`${testidPrefix}-list`}>
      <div className="px-4 py-2 border-b border-ink-border flex items-center gap-2 text-[10px] uppercase font-black text-ink-textDim">
        {icon} {title} ({items.length})
      </div>
      {items.length === 0 ? (
        <div className="p-4 text-xs text-ink-textDim text-center italic">{emptyMsg}</div>
      ) : (
        <div className="divide-y divide-ink-border">
          {items.map((it, idx) => (
            <div key={it.event_id || it.refund_id || idx} className="px-4 py-2 flex items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase text-ink-textFaint font-black">{it.type || it.flow || ""}</div>
                <div className="text-[11px] text-ink-textDim truncate">{it.created_at?.slice(0, 19).replace("T", " ")}</div>
              </div>
              <div className="text-end shrink-0">{renderRow(it)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
